import PropTypes from 'prop-types';
import { useSiteContext } from '@eti/providers';
import { LoadingDots } from '@eti/components';
import { css, cx, etiColors } from '@eti/styles';
import { breakpoints, Column, Columns, Heading, Stack } from '@etg/wings';
import { endpoints } from '../../../../constants/routesAndApisConstants';
import { STATUS, useJson } from '../../../../common/custom-hooks/json';
import { linkStyles } from '../../../../utils/styleUtils';
import PageSection from './PageSection';

const buttonsWrapperStyles = css`
  @media (min-width: ${breakpoints._768}) {
    justify-content: flex-start;
  }
`;

const columnsStyles = css`
  row-gap: 0;
`;

const loadingDotsStyles = css`
  font-size: 20px; /* stylelint-disable-line declaration-property-unit-allowed-list */
  text-align: center;
`;

const stackStyles = css`
  margin-bottom: 16px;
`;

const aTagStyles = css`
  border-bottom: 1px solid ${etiColors.grayScale[4]};
  display: block;
  padding: 16px 0;
`;

const getLanguageParam = (language, market) => {
  if (
    (language.code === 'el' && market.code === 'GR') ||
    (language.code === 'en' && market.code === 'GB') ||
    (language.code === 'es' && market.code === 'MX') ||
    (language.code === 'pt' && market.code === 'BR') ||
    (language.code === 'zh' && market.code === 'TW')
  ) {
    return `${language.code}-${market.code}`;
  }

  return language.code;
};

const Faq = ({
  buttonLink,
  buttonText,
  maxQuestionsCount,
  numberOfColumns,
  subtitle,
  tag,
  testIdPrefix,
  title,
}) => {
  const { language, market } = useSiteContext();

  const languageParam = getLanguageParam(language, market);
  const url = `${endpoints.AVAILABLE_FAQS}?language=${languageParam}&tag=${tag}`;
  const { status, data } = useJson(url);
  const questions = data?.model;

  const faqButton = {
    text: buttonText,
    as: 'a',
    href: buttonLink,
    target: '_blank',
    dataTestId: `${testIdPrefix}-faq-button`,
    variant: 'secondary',
  };

  return (
    <PageSection.Body
      buttons={[faqButton]}
      buttonsLayout="horizontal"
      classNames={{ buttonsWrapper: buttonsWrapperStyles }}
      dataTestId={`${testIdPrefix}-faq`}
    >
      <Stack className={stackStyles}>
        {title && (
          <Heading as="h2" data-testid={`${testIdPrefix}-faq-title`} level={3}>
            {title}
          </Heading>
        )}
        {subtitle && <div data-testid={`${testIdPrefix}-faq-subtitle`}>{subtitle}</div>}
        {status === STATUS.PENDING && <LoadingDots containerStyle={loadingDotsStyles} />}
        {status === STATUS.RESOLVED && questions?.length > 0 && (
          <Columns
            className={columnsStyles}
            collapseBelow={breakpoints._970}
            data-testid={`${testIdPrefix}-faq-list`}
            spacing={16}
          >
            {questions.slice(0, maxQuestionsCount).map((question) => (
              <Column
                key={question.title}
                width={{
                  [breakpoints._0]: '4/4',
                  [breakpoints._970]: `2/${2 * numberOfColumns}`,
                }}
              >
                <a
                  className={cx(linkStyles, aTagStyles)}
                  href={question.url}
                  rel="noreferrer"
                  target="_blank"
                >
                  {question.title}
                </a>
              </Column>
            ))}
          </Columns>
        )}
      </Stack>
    </PageSection.Body>
  );
};

Faq.propTypes = {
  buttonLink: PropTypes.string,
  buttonText: PropTypes.string,
  maxQuestionsCount: PropTypes.number,
  numberOfColumns: PropTypes.oneOf([1, 2]),
  subtitle: PropTypes.string,
  tag: PropTypes.string,
  testIdPrefix: PropTypes.string,
  title: PropTypes.string,
};

export default Faq;
